import { useEffect, useState } from 'react';
import QuickTrainTimeMachine from '../components/QuickTrainTimeMachine';
import { debug } from '../utils/debug';

/**
 * Test Page for QuickTrainTimeMachine Component
 * Displays the time machine component and allows testing with different locations
 */
export default function TimeMachineTest() {
  const [locationId, setLocationId] = useState(() => {
    const stored = localStorage.getItem('ghl_location_id');
    return stored || '6SevVxgW8tDgU1mSBKaj'; // Default location
  });

  const [selectedExample, setSelectedExample] = useState(null);
  const [alert, setAlert] = useState(null);

  const showAlert = (message, type = 'info') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  useEffect(() => {
    // Store location ID when changed
    if (locationId) {
      localStorage.setItem('ghl_location_id', locationId);
    }
  }, [locationId]);

  const handleSelectExample = (example) => {
    setSelectedExample(example);
    debug.log('[TimeMachineTest] Selected example:', example);

    // Show toast-like notification
    showAlert(
      `Selected: ${example.sender_email || example.sender_name || '(no sender)'} | ${example.subject || '(no subject)'} | ${example.is_spam ? 'SPAM' : 'LEGITIMATE'}`,
      'info'
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Styled Alert */}
        {alert && (
          <div className={`rounded-lg p-4 border transition-all ${
            alert.type === 'error'
              ? 'bg-red-500/20 border-red-500 text-red-200'
              : alert.type === 'warning'
              ? 'bg-yellow-500/20 border-yellow-500 text-yellow-200'
              : 'bg-blue-500/20 border-blue-500 text-blue-200'
          }`}>
            <p className="font-medium">{alert.message}</p>
          </div>
        )}

        {/* Header */}
        <div>
          <h1 className="text-5xl font-bold mb-3">🔮 Quick Train Time Machine</h1>
          <p className="text-lg opacity-80">
            Browse and review all training examples your AI has learned from. Perfect for debugging detection patterns.
          </p>
        </div>

        {/* Location Selector */}
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
          <h2 className="text-xl font-bold mb-4">📍 Location Selection</h2>
          <p className="text-sm opacity-80 mb-3">
            Enter a location ID to view its training examples. Your current location is saved automatically.
          </p>
          <div className="flex gap-3">
            <input
              type="text"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              placeholder="Enter location ID..."
              className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg border border-white/20 focus:border-blue-500 focus:outline-none"
            />
            <button
              onClick={() => {
                localStorage.setItem('ghl_location_id', locationId);
                window.location.reload();
              }}
              className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold whitespace-nowrap"
            >
              Load Location
            </button>
          </div>

          <div className="mt-4 text-sm opacity-70">
            <strong>Default:</strong> 6SevVxgW8tDgU1mSBKaj (Milton Ecom)
          </div>
        </div>

        {/* Main Component */}
        <div>
          <h2 className="text-2xl font-bold mb-4">📂 Training History Browser</h2>
          <QuickTrainTimeMachine locationId={locationId} onSelectExample={handleSelectExample} />
        </div>

        {/* Selected Example Display */}
        {selectedExample && (
          <div className="bg-purple-500/20 border border-purple-500 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-4">📋 Selected Example</h2>
            <div className="bg-gray-800/50 rounded-lg p-4 font-mono text-sm space-y-2">
              <div>
                <span className="opacity-60">Type:</span>{' '}
                {selectedExample.is_spam ? '🚨 SPAM' : '✅ LEGITIMATE'}
              </div>
              <div>
                <span className="opacity-60">Sender:</span> {selectedExample.sender_email}
              </div>
              <div>
                <span className="opacity-60">Name:</span> {selectedExample.sender_name}
              </div>
              <div>
                <span className="opacity-60">Subject:</span> {selectedExample.subject}
              </div>
              <div>
                <span className="opacity-60">Confidence:</span>{' '}
                {selectedExample.confidence ? (selectedExample.confidence * 100).toFixed(0) : 'N/A'}%
              </div>
              <div>
                <span className="opacity-60">Marked:</span> {selectedExample.marked_at}
              </div>
            </div>

            <button
              onClick={() => setSelectedExample(null)}
              className="mt-4 px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-semibold"
            >
              Clear Selection
            </button>
          </div>
        )}

        {/* Info Section */}
        <div className="bg-blue-500/10 border border-blue-500 rounded-lg p-6">
          <h3 className="text-xl font-bold mb-3">ℹ️ How It Works</h3>
          <ul className="space-y-2 text-sm opacity-90">
            <li>
              <strong>Browse:</strong> Use the Previous/Next buttons or click thumbnails to browse all training examples.
            </li>
            <li>
              <strong>Stats:</strong> See totals at the top - total examples, spam count, legitimate count, and spam ratio.
            </li>
            <li>
              <strong>Balance:</strong> Aim for a balanced mix of spam and legitimate examples for best AI accuracy.
            </li>
            <li>
              <strong>Use Example:</strong> Click "Use This Example" to select an example (shown in the panel below).
            </li>
            <li>
              <strong>Feedback:</strong> These examples are injected into OpenAI prompts to improve future detections.
            </li>
          </ul>
        </div>

        {/* Testing Tips */}
        <div className="bg-green-500/10 border border-green-500 rounded-lg p-6">
          <h3 className="text-xl font-bold mb-3">🧪 Testing Tips</h3>
          <ul className="space-y-2 text-sm opacity-90">
            <li>✅ Watch for patterns in spam examples - similar phishing techniques</li>
            <li>✅ Check sender domains - many spam examples share similar patterns</li>
            <li>✅ Look at subjects - notice recurring keywords like "verify", "confirm", "urgent"</li>
            <li>✅ Review legitimacy - are legitimate examples diverse enough?</li>
            <li>⚠️ If spam ratio is 100%, add more legitimate examples to balance</li>
            <li>⚠️ If you see false positives in history, consider removing them</li>
          </ul>
        </div>

        {/* API Info */}
        <div className="bg-gray-700/30 border border-white/10 rounded-lg p-6">
          <h3 className="text-xl font-bold mb-3">🔌 API Reference</h3>
          <p className="text-sm opacity-80 mb-3">
            The component fetches training examples from the backend:
          </p>
          <div className="bg-black/50 rounded p-3 font-mono text-xs overflow-x-auto mb-3">
            GET /api/spam-rules/{'{'}location_id{'}'}/training-examples
          </div>
          <p className="text-xs opacity-70">
            Returns a list of training examples with: sender_email, sender_name, subject, body_preview, is_spam,
            confidence, marked_at
          </p>
        </div>
      </div>
    </div>
  );
}
